import Header from "../components/header";
import mainStore from "../../store/store";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopCars from "../components/topcars";
import Footer from "../components/footer";
import Loader from '../components/loader';
import { useCMSContent } from "../../hooks/useCMSContent";

import moment from "moment";
import { timeOptions, getTimeOptionsForLocation, getDropoffTimeOptions, isPerthOfficeHoursPassed, getDefaultPickupDateForLocation, getPerthDateToday, getCurrentPerthTimeRounded, getDefaultPickupTimeForLocation } from "../../utils/constants";

const homeFaqs = [
  {
    question: "Can I rent a car by the hour in Perth?",
    answer:
      "TAK8 Car Rental has a minimum hire period of 24 hours. Select your preferred pickup and return times to check vehicle availability for your dates.",
  },
  {
    question: "Does TAK8 Car Rental offer long-term car rental?",
    answer:
      "Long-term and extended rental options may be available when you need a vehicle for several weeks or longer. Contact TAK8 Car Rental to discuss your dates and current vehicle availability.",
  },
  {
    question: "Where is TAK8 Car Rental located?",
    answer:
      "TAK8 Car Rental's head office is located at 17 Regal Place, East Perth, Western Australia. Use the directions link in the website footer when planning your visit.",
  },
  {
    question: "Is Perth Airport car rental pickup available?",
    answer:
      "Perth Airport car rental pickup can be arranged subject to availability, timing and the conditions confirmed during booking. Contact TAK8 Car Rental before your arrival to confirm the arrangement.",
  },
  {
    question: "What documents do I need to rent a car?",
    answer:
      "Rental eligibility and document requirements may vary. Review TAK8 Car Rental's current rental terms or contact our team to confirm the licence, identification and payment requirements for your booking.",
  },
  {
    question: "How can I check current prices and vehicle availability?",
    answer:
      "Enter your pickup and return locations, dates and times in the booking form. The website will display the vehicles and prices available for your selected rental period.",
  },
];

const homeServices = [
  {
    title: "Perth Airport Car Hire Arrangements",
    description:
      "Flying into Perth Airport? Contact TAK8 Car Rental before booking to discuss available airport car hire arrangements, collection instructions and a suitable rental vehicle for your travel dates.",
    link: "/services#airport",
    linkLabel: "Airport Car Hire Details",
    imageAlt: "TAK8 Car Rental Perth Airport car hire arrangements",
  },
  {
    title: "Car Rental Vehicles in Perth",
    description:
      "Choose from automatic compact cars, sedans, SUVs, 7-seaters, 8-seaters and utes for local travel, business trips and longer journeys. Availability depends on your selected dates.",
    link: "/services#vrs",
    linkLabel: "Explore Rental Vehicles",
    imageAlt: "TAK8 Car Rental vehicles available in Perth",
  },
  {
    title: "Extended Car Rental Options",
    description:
      "Need a vehicle for a week or longer? Ask TAK8 Car Rental about suitable vehicles, current availability and extended-rental pricing for your plans around Perth and Western Australia.",
    link: "/services#dis",
    linkLabel: "Extended Rental Details",
    imageAlt: "TAK8 Car Rental extended car rental options in Perth",
  },
];

const homeAdvantages = [
  {
    title: "Simple Online Booking",
    description:
      "Check available vehicles and choose your rental dates through the TAK8 Car Rental booking form.",
    icon: "img/icon/rent.svg",
  },
  {
    title: "Well-Maintained Vehicles",
    description:
      "Rent clean, practical vehicles maintained for comfortable travel around Perth.",
    icon: "img/icon/premium.svg",
  },
  {
    title: "East Perth Location",
    description:
      "Collect your vehicle from our head office at 17 Regal Place, East Perth.",
    icon: "img/icon/agent.svg",
  },
  {
    title: "Flexible Rental Periods",
    description:
      "Choose short-term, daily or longer rental periods according to your plans.",
    icon: "img/icon/safety.svg",
  },
  {
    title: "Clear Rental Information",
    description:
      "Review vehicle prices, availability and rental conditions before confirming your booking.",
    icon: "img/icon/refund.svg",
  },
  {
    title: "Local Customer Support",
    description:
      "Contact the TAK8 Car Rental team when you need assistance before or during your rental.",
    icon: "img/icon/live.svg",
  },
];

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [showErrorOnPickup, setShowErrorOnPickup] = useState(false);
  const [showErrorOnDropOff, setShowErrorOnDropOff] = useState(false);
  
  // CMS Content Hook - fetches content from API, falls back to hardcoded if unavailable
  const { cmsContent, getSectionContent, getSectionItems, hasCMSContent } = useCMSContent('home');
  const makeRoundTime = (time) => {
    console.log('time', time);

    // Parse the input time - try both 24h and 12h formats
    let parsedTime = moment(time, ["HH:mm", "hh:mm A", "h:mm A"]);

    if (!parsedTime.isValid()) {
      // Fallback: just return a default time
      return "09:00 AM";
    }

    // Check if minutes are greater than 0 and round up
    if (parsedTime.minute() > 0) {
      parsedTime = parsedTime.add(1, "hour").minute(0).second(0);
    } else {
      parsedTime = parsedTime.minute(0).second(0);
    }

    // Return the formatted rounded time
    return parsedTime.format("hh:mm A");
  }
  const setupGoogleReviews = () => {
    // Elfsight uses ResizeObserver internally. Load its platform only once and
    // always clean up our own observer/timer when React remounts this page.
    const scriptId = "tak8-elfsight-platform";
    const scriptSrc = "https://static.elfsight.com/platform/platform.js";
    let observer = null;
    let fallbackTimer = null;
    let disposed = false;

    const handleElfsightResizeNotification = (event) => {
      const isElfsightResizeNotification =
        event.message ===
          "ResizeObserver loop completed with undelivered notifications." &&
        document.getElementById(scriptId);

      if (isElfsightResizeNotification) {
        // The browser has already deferred the remaining widget observations to
        // the next frame. Prevent webpack-dev-server from treating this
        // third-party layout notification as an application crash.
        event.stopImmediatePropagation();
      }
    };

    window.addEventListener(
      "error",
      handleElfsightResizeNotification,
      true
    );

    const initialiseExistingWidget = () => {
      if (typeof window.eapps?.init === "function") {
        window.requestAnimationFrame(() => {
          if (!disposed) window.eapps.init();
        });
      }
    };

    const loadElfsight = () => {
      if (disposed) return;

      const existingScript =
        document.getElementById(scriptId) ||
        document.querySelector(`script[src="${scriptSrc}"]`);

      if (existingScript) {
        initialiseExistingWidget();
        return;
      }

      const script = document.createElement("script");
      script.id = scriptId;
      script.src = scriptSrc;
      script.async = true;
      script.defer = true;
      script.addEventListener("load", initialiseExistingWidget, { once: true });
      document.body.appendChild(script);
    };

    const testimonialSection = document.querySelector(".TestimonialsSection");

    if (testimonialSection && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            loadElfsight();
            observer?.disconnect();
            observer = null;
          }
        },
        { rootMargin: "200px" }
      );
      observer.observe(testimonialSection);
    } else {
      // Fallback for older browsers or an unexpectedly delayed section render.
      fallbackTimer = window.setTimeout(loadElfsight, 3000);
    }

    return () => {
      disposed = true;
      observer?.disconnect();
      if (fallbackTimer !== null) {
        window.clearTimeout(fallbackTimer);
      }
      window.removeEventListener(
        "error",
        handleElfsightResizeNotification,
        true
      );
    };
  };

  const navigate = useNavigate();
  const { userBooking, setUserBooking, selectedRoute, setSelectedRoute } = mainStore();
  const routeTo = (routeTo) => {
    setSelectedRoute(routeTo);
    navigate(routeTo);
  };
  const [findRide, setFindRide] = useState({
    pickUpLocation: "",
    pickUpDate: getPerthDateToday(),
    pickUpTime: "08:00 AM",
    returnLocation: "",
    returnDate: moment(getPerthDateToday()).add(7, "days").format("YYYY-MM-DD"),
    returnTime: "08:00 AM",
    driverAge: "",
    agreeToTerms: true,
  });

  useEffect(() => {
    const cleanupGoogleReviews = setupGoogleReviews();
    setLoading(false);

    // SEO Meta Tags
    // Set document title
    document.title = "Car Rental Perth From $25/Day | TAK8 Car Rental";
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Affordable car rental in Perth from $25/day with TAK8 Car Rental in East Perth. Available on selected long-term rentals of 28 days or more. Explore our Perth cars online.'
      );
    }
    
    // Set or create Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', 'Car Rental Perth From $25/Day | TAK8 Car Rental');
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', 'Affordable car rental in Perth from $25/day with TAK8 Car Rental in East Perth. Available on selected long-term rentals of 28 days or more. Explore our Perth cars online.');
    
    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) {
      ogType = document.createElement('meta');
      ogType.setAttribute('property', 'og:type');
      document.head.appendChild(ogType);
    }
    ogType.setAttribute('content', 'website');

    const homeOpenGraphTags = {
      'og:url': 'https://tak8.com.au/',
      'og:site_name': 'TAK8 Car Rental',
      'og:locale': 'en_AU',
      'og:image': 'https://tak8.com.au/img/service_banner.png',
      'og:image:width': '2880',
      'og:image:height': '1504',
      'og:image:alt': 'TAK8 Car Rental vehicles in Perth',
    };

    Object.entries(homeOpenGraphTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    return cleanupGoogleReviews;
  }, []);

  const onInputChange = (e) => {

    const { name, value } = e.target;
    if (name === "pickUpLocation") {
      findRide.returnLocation = value;
      // Reset times if they fall outside valid range for the new location
      const validTimes = getTimeOptionsForLocation(value);
      const updatedState = { ...findRide, [name]: value, returnLocation: value };
      
      if (value === "Perth Airport") {
        // Perth Airport: use current Perth time (rounded)
        const currentPerthTime = getCurrentPerthTimeRounded();
        updatedState.pickUpTime = currentPerthTime;
        updatedState.returnTime = currentPerthTime;
        // Date should be Perth's today
        updatedState.pickUpDate = getPerthDateToday();
        updatedState.returnDate = moment(getPerthDateToday()).add(7, "days").format("YYYY-MM-DD");
      } else {
        // East Perth / Crown Plaza: use current Perth time if within office hours
        const defaultTime = getDefaultPickupTimeForLocation(value);
        updatedState.pickUpTime = defaultTime;
        // Drop-off time stays as is (full 24hr allowed)
        
        // Auto-bump pickup date to tomorrow if office hours have passed
        if (isPerthOfficeHoursPassed()) {
          const perthToday = getPerthDateToday();
          if (updatedState.pickUpDate <= perthToday) {
            const correctDate = getDefaultPickupDateForLocation(value);
            updatedState.pickUpDate = correctDate;
            updatedState.returnDate = moment(correctDate).add(7, "days").format("YYYY-MM-DD");
          }
        }
      }
      
      setFindRide(updatedState);
      return;
    }
    if (name === "returnLocation") {
      // Drop-off allows all times, no restriction needed
      setFindRide({ ...findRide, [name]: value });
      return;
    }
    if (name === "pickUpDate") {
      onSelectingPickUpDate(e);
    } else {
      setFindRide({ ...findRide, [name]: value });
    }

  };

  const onSelectingPickUpDate = (e) => {
    const pickUpDateTime = moment(
      `${e.target.value}T${moment().format("hh:mm")}`
    );
    const returnDateTime = pickUpDateTime.add(7, "days");

    setFindRide({
      ...findRide,
      pickUpDate: e.target.value,
      returnDate: moment(returnDateTime).format("YYYY-MM-DD"),
    });
  };
  const validateForm = () => {
    if (findRide.pickUpLocation == "") {
      setShowErrorOnPickup(true);
      return false;
    }
    else {
      setShowErrorOnPickup(false);
    }
    if (findRide.returnLocation == "") {
      setShowErrorOnDropOff(true);// = true;
      return false;
    }
    else {
      setShowErrorOnDropOff(false);
    }

    return true;
  }
  const onFindCar = () => {
    if (!validateForm()) {
      return;
    }
    const booking = { ...userBooking };
    booking.booking = { ...booking.booking, ...findRide };
    setUserBooking(booking);
    navigate("/cars?find=1");
  };

  return (
    <>
      {loading ? <Loader /> : ""}
      <Header />
      <section className="SearchSection SearchHeroSection" id="booking-search">
        <div className="container">
          <div className="SearchHeroCopy">
            <span className="SearchHeroEyebrow">TAK8 Car Rental · EAST PERTH</span>
            <h1 className="SearchHeroTitle">
              <span>
                Car Rental Perth <span className="SearchHeroTitleDash"></span>{""}
              </span>{" "}
              <strong>Affordable Car<span className="SearchHeroTitleDash"> Hire From $25/Day</span></strong>
            </h1>
            <p className="SearchHeroSlogan">
              Daily, weekly and long-term car rental from our convenient East Perth location.
            </p>
            <p className="SearchHeroIntro">
              Choose from compact cars, sedans, SUVs, utes, 7-seaters and 8-seaters for travel across Perth and Western Australia.
            </p>
          </div>

          <form className="SearchForm SearchHeroForm">
            <div className="row">

              <div class="form-field location-field col-md-6 mt-2">
                <label for="PickupLocation" class="form-label">
                  <i class="fas fa-map-marker-alt"></i>
                  Pickup Location
                </label>
                <div className={`input-container tooltip-wrapper ${showErrorOnPickup ? 'error' : ''}`}
                  data-tooltip={showErrorOnPickup ? 'Pick-up location is required' : ''}>
                  <select
                    className="form-select"
                    id="PickupLocation"
                    name="pickUpLocation"
                    value={findRide.pickUpLocation}
                    onChange={onInputChange}
                   
                  >
                    <option value="">Select Location</option>
                    <option value="East Perth">East Perth</option>
                    <option value="Crown Plaza">Crown Plaza</option>
                    <option value="Perth Airport">Perth Airport</option>
                  </select>
                </div>
              </div>

              <div class="form-field datetime-field col-md-6 mt-2">
                <label for="PickupTime" class="form-label">
                  <i class="fas fa-calendar-alt"></i>
                  Pickup Date & Time
                </label>
                <div class="datetime-group">
                  <div class="input-container">
                    <input
                      type="date"
                      class="form-control leftAlignText"
                      id="PickupTime"
                      name="pickUpDate"
                      value={findRide.pickUpDate}
                      onChange={onInputChange}
                      min={new Date().toISOString().split("T")[0]}

                    />
                  </div>
                  <div class="input-container time-input">
                    <select
                      class="form-control"
                      name="pickUpTime"
                      onChange={onInputChange}
                      value={findRide.pickUpTime}
                    >
                      {getTimeOptionsForLocation(findRide.pickUpLocation).map((time) => (
                        <option value={time.value}>{time.label}</option>
                      ))}
                    </select>

                  </div>
                </div>
              </div>

              <div class="form-field location-field col-md-6 mt-2 ">
                <label for="returnLocation" class="form-label ">
                  <i class="fas fa-map-marker-alt"></i>
                  Drop-off Location
                
                </label>
                <div className={`input-container tooltip-wrapper ${showErrorOnDropOff ? 'error' : ''}`}
                  data-tooltip={showErrorOnDropOff ? 'Drop-off location is required' : ''}>
                  <select
                    class="form-select"
                    id="returnLocation"
                    name="returnLocation"
                    value={findRide.returnLocation}
                    onChange={onInputChange}
                  >
                    <option value="">Select Location</option>
                    <option value="East Perth">East Perth</option>
                    <option value="Crown Plaza">Crown Plaza</option>
                    <option value="Perth Airport">Perth Airport</option>
                  </select>
                </div>
              </div>

              <div class="form-field datetime-field col-md-6 mt-2">
                <label for="DropOffTime" class="form-label">
                  <i class="fas fa-calendar-alt"></i>
                  Drop-off Date & Time
                </label>
                <div class="datetime-group">
                  <div class="input-container">
                    <input
                      type="date"
                      class="form-control"
                      id="returnDate"
                      name="returnDate"
                      value={findRide.returnDate}
                      onChange={onInputChange}
                      min={findRide.pickUpDate}
                    />
                  </div>
                  <div class="input-container time-input">
                    <select
                      class="form-control"
                      name="returnTime"
                      value={findRide.returnTime}
                      onChange={onInputChange}
                    >
                      {getDropoffTimeOptions().map((time) => (
                        <option value={time.value}>{time.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>


            </div>
            <div className="SearchHeroFormFooter">
              <p>
                <i className="fas fa-check-circle" aria-hidden="true"></i>
                Simple booking with clear availability
              </p>
              <button 
                data-analytics-name="Find My Ride - Homepage Search"
                className="search-btn" 
                type="button" 
                onClick={() => onFindCar()}
              >
                Find My Ride
                <i className="fas fa-chevron-right" aria-hidden="true"></i>
              </button>
            </div>
          </form>
        </div>
      </section>

      <section class="HomeSection">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-6">
              <div className="HomeLongTermOffer">
                <span className="HomeLongTermOfferEyebrow">
                  Best Car Rental Perth
                </span>
                <h2 className="HomeLongTermOfferTitle">
                 Cheap Car Hire Perth {" "}
                  <strong>From $25/Day</strong>
                </h2>

                <ul className="HomeLongTermOfferBenefits">
                  <li>Long-Term Car Hire from $25/Day</li>
                  <li>Comprehensive Insurance Included</li>
                  <li>Flexible daily, weekly and monthly rentals</li>
                  <li>No Hidden Booking Fees</li>
                  <li>Convenient East Perth Location</li>
                </ul>

                <p className="HomeLongTermOfferFinePrint">
                  <strong>Rates from $25/day apply to rentals of 28 days or more.</strong>{" "}
                  T&Cs apply. Subject to vehicle availability.
                </p>

                <div className="HomeJourneySupport">
                  <span>
                    <strong>Drive</strong> Your Journey
                  </span>
                  <span>Leave the Rest to Us!</span>
                </div>
              </div>

              <div class="Button HomeLongTermOfferActions">
                <a
                  href="#booking-search"
                  class="BtnFill"
                >
                  Check Availability
                </a>
                <a
                  href="/carslist"
                  onClick={(event) => {
                    event.preventDefault();
                    routeTo("/carslist");
                  }}
                  class="BtnBorderY"
                >
                  Our Cars
                </a>
              </div>
            </div>
            <div class="col-lg-6 HomeJourneyVisual">
              <img src="img/home_car.png" alt="TAK8 Car Rental vehicle in Perth" width="700" height="400" />
              <img src="img/home_car_mob.png" alt="TAK8 Car Rental vehicle in Perth" width="350" height="250" />
            </div>
          </div>
        </div>
      </section>
      <section className="TopCarsHomeSection">
        <TopCars />
      </section>

      <section className="RentalOptionsSection" aria-labelledby="rental-options-heading">
        <div className="container">
          <div className="RentalOptionsHeading">
            <span>FLEXIBLE PERTH CAR HIRE</span>
            <h2 id="rental-options-heading">
              Choose a Rental Period <span>That Suits Your Plans</span>
            </h2>
            <p>
              From short local trips to extended travel, check TAK8 Car Rental vehicle
              availability for the time you need.
            </p>
          </div>
          <div className="RentalOptionsGrid">
            <article className="RentalOptionCard">
              <div className="RentalOptionIcon">
                <img src="/img/icon/clock.svg" alt="" aria-hidden="true" />
              </div>
              <span className="RentalOptionNumber">01</span>
              <h3>Short-Term Car Rental</h3>
              <p>
                Need a vehicle for a short period? Check available TAK8 Car Rental
                vehicles for appointments, local trips and temporary transport
                around Perth.
              </p>
              <a className="RentalOptionLink" href="#booking-search">
                Check Availability <span aria-hidden="true">→</span>
              </a>
            </article>

            <article className="RentalOptionCard RentalOptionCardFeatured">
              <div className="RentalOptionIcon">
                <img src="/img/icon/car.svg" alt="" aria-hidden="true" />
              </div>
              <span className="RentalOptionNumber">02</span>
              <h3>Daily Car Rental</h3>
              <p>
                Choose a practical rental vehicle for day trips, work
                commitments or exploring Perth and Western Australia.
              </p>
              <a
                className="RentalOptionLink"
                href="/carslist"
                onClick={(event) => {
                  event.preventDefault();
                  routeTo("/carslist");
                }}
              >
                Browse Cars <span aria-hidden="true">→</span>
              </a>
            </article>

            <article className="RentalOptionCard">
              <div className="RentalOptionIcon">
                <img src="/img/icon/rent.svg" alt="" aria-hidden="true" />
              </div>
              <span className="RentalOptionNumber">03</span>
              <h3>Long-Term Car Rental</h3>
              <p>
                Need a vehicle for several weeks or longer? Contact TAK8 Car Rental to
                discuss extended rental availability and suitable vehicles.
              </p>
              <a
                className="RentalOptionLink"
                href="/contact-us"
                onClick={(event) => {
                  event.preventDefault();
                  routeTo("/contact-us");
                }}
              >
                Contact TAK8 Car Rental <span aria-hidden="true">→</span>
              </a>
            </article>
          </div>
        </div>
      </section>

      <section
        className="RentalInfoSection"
        aria-labelledby="rental-information-heading"
      >
        <div className="container">
          <div className="RentalInfoHeading">
            <span>BEFORE YOU BOOK</span>
            <h2 id="rental-information-heading">
              Important <span>Rental Information</span>
            </h2>
            <p>
              Review the key requirements below and check the complete rental
              terms for the conditions confirmed with your booking.
            </p>
          </div>

          <div className="RentalInfoGrid">
            <article className="RentalInfoCard">
              <img src="/img/icon/driver.svg" alt="" aria-hidden="true" />
              <h3>Drivers and licences</h3>
              <p>
                The renter and every additional driver must hold a valid
                licence and be authorised on the rental agreement.
              </p>
            </article>

            <article className="RentalInfoCard">
              <img src="/img/icon/location.svg" alt="" aria-hidden="true" />
              <h3>Pickup and return</h3>
              <p>
                Collect and return the vehicle at the TAK8 Car Rental depot and times
                confirmed for your booking.
              </p>
            </article>

            <article className="RentalInfoCard">
              <img src="/img/icon/safety.svg" alt="" aria-hidden="true" />
              <h3>Cover and bond</h3>
              <p>
                Available cover, liability and security-bond requirements
                depend on the option confirmed for your rental.
              </p>
            </article>
          </div>

          <nav className="RentalInfoLinks" aria-label="Rental information links">
            <a href="/terms-conditions">Read rental terms</a>
          </nav>
        </div>
      </section>

      {/* <section class="LogoSection">
        <div class="container">
          <img src="img/logo_1.png" alt="Logo" />
          <img src="img/logo_2.png" alt="Logo" />
          <img src="img/logo_3.png" alt="Logo" />
          <img src="img/logo_4.png" alt="Logo" />
          <img src="img/logo_5.png" alt="Logo" />
          <img src="img/logo_6.png" alt="Logo" />
          <img src="img/logo_7.png" alt="Logo" />
          <img src="img/logo_8.png" alt="Logo" />
        </div>
      </section> */}

      {/* <section class="CarSection">
        <div class="container">
          <div class="CustomHeading">
            <h2 class="Head_1">POPULAR CAR</h2>
            <h3 class="Head_2">Choose Your Suitable Car</h3>
            <p class="Pra_1">
              We present popular cars that are rented by customers to maximize
              your comfort on long trips.
            </p>
          </div>
          <div class="row justify-content-md-center">
            <div class="col-md-4">
              <div class="CarBox">
                <div class="Image">
                  <img src="img/car_1.png" alt="car" />
                  <span class="Tag Red">Automatic</span>
                </div>
                <div class="Content">
                  <div class="Heading">
                    <h4 class="Head_3">Swift</h4>
                    <div class="Features">
                      <img src="img/icon/air_conditioning.svg" alt="Icon" />
                      <img src="img/icon/gps_navigation.svg" alt="Icon" />
                      <img src="img/icon/bluetooth.svg" alt="Icon" />
                      <img src="img/icon/camera.svg" alt="Icon" />
                      <img src="img/icon/cruise_control.svg" alt="Icon" />
                    </div>
                  </div>
                  <div class="Price">
                    <h5>
                      $85<span>/Day</span>
                    </h5>
                    <p>
                      <img src="img/icon/car.svg" alt="Car" />
                      Suzuki 2024
                    </p>
                  </div>
                  <a href="#" class="BtnBorder">
                    Booking Details
                  </a>
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="CarBox">
                <div class="Image">
                  <img src="img/car_2.png" alt="car" />
                  <span class="Tag Yellow">Manual</span>
                </div>
                <div class="Content">
                  <div class="Heading">
                    <h4 class="Head_3">Nissan Altima</h4>
                    <div class="Features">
                      <img src="img/icon/air_conditioning.svg" alt="Icon" />
                      <img src="img/icon/gps_navigation.svg" alt="Icon" />
                      <img src="img/icon/bluetooth.svg" alt="Icon" />
                      <img src="img/icon/camera.svg" alt="Icon" />
                      <img src="img/icon/cruise_control.svg" alt="Icon" />
                    </div>
                  </div>
                  <div class="Price">
                    <h5>
                      $120<span>/Day</span>
                    </h5>
                    <p>
                      <img src="img/icon/car.svg" alt="Car" />
                      Nissan 2024
                    </p>
                  </div>
                  <a href="#" class="BtnBorder">
                    Booking Details
                  </a>
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="CarBox">
                <div class="Image">
                  <img src="img/car_3.png" alt="car" />
                  <span class="Tag Red">Automatic</span>
                </div>
                <div class="Content">
                  <div class="Heading">
                    <h4 class="Head_3">Lexus</h4>
                    <div class="Features">
                      <img src="img/icon/air_conditioning.svg" alt="Icon" />
                      <img src="img/icon/gps_navigation.svg" alt="Icon" />
                      <img src="img/icon/bluetooth.svg" alt="Icon" />
                      <img src="img/icon/camera.svg" alt="Icon" />
                      <img src="img/icon/cruise_control.svg" alt="Icon" />
                    </div>
                  </div>
                  <div class="Price">
                    <h5>
                      $150<span>/Day</span>
                    </h5>
                    <p>
                      <img src="img/icon/car.svg" alt="Car" />
                      Toyota 2024
                    </p>
                  </div>
                  <a href="#" class="BtnBorder">
                    Booking Details
                  </a>
                </div>
              </div>
            </div>
            <a href="#" class="Button BtnFill">
              See All
            </a>
          </div>
        </div>
      </section> */}

      <section class="ServicesSection">
        <div class="container">
          <div class="CustomHeading">

            <h2 class="Head_2">
              Car Rental <span>Services in Perth</span>
            </h2>
            <p className="Pra_1">
              Explore TAK8 Car Rental options from East Perth, including airport
              pickup arrangements, practical vehicles and extended hire for
              longer travel plans.
            </p>
          </div>
          <div class="row">
            {(() => {
              const servicesItems = getSectionItems('services');
              if (servicesItems && servicesItems.length > 0) {
                return homeServices.map((reviewedService, index) => {
                  const service = servicesItems[index] || {};
                  // Handle icon path - if it's just a name, add the full path
                  const iconPath = service.icon 
                    ? (service.icon.startsWith('img/') ? service.icon : `img/icon/${service.icon}.svg`)
                    : `img/icon/airport.svg`;
                  const serviceHref = reviewedService.link;
                  return (
                    <div class="col-md-4" key={service.id || index}>
                      <div class="ServiceBox">
                        <img
                          src={service.image || `img/service_${index + 1}.png`}
                          class="BoxImg"
                          alt={reviewedService.imageAlt}
                          loading="lazy"
                        />
                        <div class="Content">
                          <div class="Details">
                            <img src={iconPath} alt="Icon" />
                            <h3 class="Head_3">{reviewedService.title}</h3>
                            <p className="Pra_1">{reviewedService.description}</p>
                            <a
                              href={serviceHref}
                              onClick={(event) => {
                                event.preventDefault();
                                routeTo(serviceHref);
                              }}
                              class="BtnFillY"
                            >
                              {reviewedService.linkLabel}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                });
              }
              // Fallback to hardcoded services
              return (
                <>
                  <div class="col-md-4">
                    <div class="ServiceBox">
                      <img src="img/service_1.png" class="BoxImg" alt="Perth Airport Car Rental Service" loading="lazy" />
                      <div class="Content">
                        <div class="Details">
                          <img src="img/icon/airport.svg" alt="Icon" />
                          <h3 class="Head_3">Perth Airport Car Hire Arrangements</h3>
                          <p class="Pra_1">
                            Flying into Perth Airport? Contact TAK8 Car Rental before booking to discuss available airport car hire arrangements, collection instructions and a suitable rental vehicle for your travel dates.
                          </p>
                          <a
                            href="/services#airport"
                            onClick={(event) => {
                              event.preventDefault();
                              routeTo("/services#airport");
                            }}
                            class="BtnFillY"
                          >
                            Airport Car Hire Details
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="ServiceBox">
                      <img src="img/service_2.png" class="BoxImg" alt="Vehicle Rental Services" loading="lazy" />
                      <div class="Content">
                        <div class="Details">
                          <img src="img/icon/vehical.svg" alt="Icon" />
                          <h3 class="Head_3">Car Rental Vehicles in Perth</h3>
                          <p class="Pra_1">
                          Choose from automatic compact cars, sedans, SUVs, 7-seaters, 8-seaters and utes for local travel, business trips and longer journeys. Availability depends on your selected dates.   </p>
                          <a
                            href="/services#vrs"
                            onClick={(event) => {
                              event.preventDefault();
                              routeTo("/services#vrs");
                            }}
                            class="BtnFillY"
                          >
                            Explore Rental Vehicles
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="ServiceBox">
                      <img src="img/service_3.png" class="BoxImg" alt="Extended car rental options in Perth" loading="lazy" />
                      <div class="Content">
                        <div class="Details">
                          <img src="img/icon/seats.svg" alt="Icon" />
                          <h3 class="Head_3">Extended Car Rental Options</h3>
                          <p class="Pra_1">
                            Need a vehicle for a week or longer? Ask TAK8 Car Rental about suitable vehicles, current availability and extended-rental pricing for your plans around Perth and Western Australia.
                          </p>
                          <a
                            href="/services#dis"
                            onClick={(event) => {
                              event.preventDefault();
                              routeTo("/services#dis");
                            }}
                            class="BtnFillY"
                          >
                            Extended Rental Details
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          <div className="HomeSectionAction">
            <a href="/contact-us" className="BtnFill">
              Ask TAK8 Car Rental
            </a>
          </div>
        </div>
      </section>

      <section
        className="HomeAirportBanner"
        aria-labelledby="home-airport-banner-heading"
      >
        <div className="container">
          <div className="HomeAirportBannerContent">
            <span className="HomeAirportBannerEyebrow">
              PERTH AIRPORT CAR RENTAL
            </span>
            <h2 id="home-airport-banner-heading">Flying into Perth Airport ?</h2>
            <p>
              Ask TAK8 Car Rental about airport car hire pickup arrangements and suitable rental
              vehicles for your travel dates.
            </p>
            <div className="HomeAirportBannerActions">
              <a
                href="/carslist"
                className="HomeAirportBannerPrimary"
                onClick={(event) => {
                  event.preventDefault();
                  routeTo("/carslist");
                }}
              >
                Check Available Cars
              </a>
              <a
                href="/contact-us"
                className="HomeAirportBannerSecondary"
                onClick={(event) => {
                  event.preventDefault();
                  routeTo("/contact-us");
                }}
              >
                Contact TAK8 Car Rental
              </a>
            </div>
          </div>
        </div>
      </section>

      <section class="AdvantagesSection">
        <div class="container">
          <div class="CustomHeading">
            <p className="Head_1">ADVANTAGES</p>
            <h2 className="Head_2">Why Choose TAK8 Car Rental?</h2>
            <p className="Pra_1">
              Practical reasons to choose TAK8 Car Rental for flexible car rental from
              our East Perth location.
            </p>
          </div>
          <div class="row">
            {(() => {
              const advantagesItems = getSectionItems('advantages');
              return homeAdvantages.map((advantage, index) => {
                const cmsIcon = advantagesItems?.[index]?.icon;
                const iconPath = cmsIcon
                  ? (cmsIcon.startsWith('img/') ? cmsIcon : `img/icon/${cmsIcon}.svg`)
                  : advantage.icon;

                return (
                  <div class="col-md-4" key={advantage.title}>
                    <div class="Box">
                      <img src={iconPath} class="BoxImg" alt="" aria-hidden="true" />
                      <div class="Content">
                        <h4 class="Head_5">{advantage.title}</h4>
                        <p className="Pra_1">{advantage.description}</p>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      <section class="TestimonialsSection">
        <div class="container">
          <div className="HomeReviewsLayout">
            <div className="HomeReviewsSummary">
              <span className="HomeReviewsEyebrow">
                {getSectionContent('testimonials', 'title', 'CUSTOMER FEEDBACK')}
              </span>
              <div
                className="Pra_1 rich-content"
                dangerouslySetInnerHTML={{
                  __html:
                    getSectionContent('testimonials', 'description', '') ||
                    getSectionContent(
                      'testimonials',
                      'content',
                      'Read feedback from customers who have rented with TAK8 Car Rental in Perth.'
                    ),
                }}
              />
              <a
                className="HomeReviewsGoogleLink"
                href="https://maps.app.goo.gl/9an8CLaNvUhWqLnW7"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="HomeReviewsGoogleWord" aria-hidden="true">
                  Google
                </span>
                <span>View all customer reviews</span>
              </a>
            </div>

            <div className="HomeReviewsWidget">
              <div
                className="elfsight-app-05bae60b-f256-4dc0-a0e0-61ba6b980858"
                data-elfsight-app-lazy
              ></div>
            </div>
          </div>
        </div>
      </section>

      <section className="HomeFaqSection" aria-labelledby="home-faq-heading">
        <div className="container">
          <div className="CustomHeading text-center">
            <span className="HomeFaqEyebrow">HELP &amp; INFORMATION</span>
            <h2 id="home-faq-heading" className="Head_2">
              Perth Car Rental FAQs
            </h2>
            <p className="Pra_1">
              Find quick answers to common questions about renting a vehicle
              with TAK8 Car Rental.
            </p>
          </div>

          <div className="HomeFaqGrid">
            {homeFaqs.map((faq, index) => (
              <details className="HomeFaqItem" key={faq.question}>
                <summary>
                  <span className="HomeFaqNumber" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span>{faq.question}</span>
                </summary>
                <div className="HomeFaqAnswer">
                  <p>{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
          <div className="HomeSectionAction">
            <a href="/faqs" className="BtnBorderY">
              Read Rental FAQs
            </a>
          </div>
        </div>
      </section>

      <section
        className="HomeLocalSection"
        aria-labelledby="home-local-business-heading"
      >
        <div className="container">
          <div className="HomeLocalLayout">
            <div className="HomeLocalContent">
              <span className="HomeLocalEyebrow">LOCAL PERTH CAR RENTAL</span>
              <h2 id="home-local-business-heading">
                Visit TAK8 Car Rental in East Perth
              </h2>
              <p className="HomeLocalIntro">
                Pick up your rental from our East Perth head office, close to
                Perth CBD. Contact our local team if you need help choosing a
                vehicle or planning your booking.
              </p>

              <div className="HomeLocalActions">
                <a
                  className="HomeLocalPrimaryAction"
                  href="https://maps.app.goo.gl/9an8CLaNvUhWqLnW7"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-directions" aria-hidden="true"></i>
                  Get Directions
                </a>
                <a
                  className="HomeLocalSecondaryAction"
                  href="tel:+61893415334"
                >
                  <i className="fas fa-phone-alt" aria-hidden="true"></i>
                  Call TAK8 Car Rental
                </a>
              </div>
            </div>
          </div>

          <div
            className="HomeMapSection"
            aria-labelledby="home-map-heading"
          >
            <div className="HomeMapLayout">
              <div className="HomeMapContent">
                <span className="HomeMapEyebrow">EAST PERTH LOCATION</span>
                <h2 id="home-map-heading">Find TAK8 Car Rental</h2>
                <div className="HomeMapCopy">
                  <p>
                    TAK8 Car Rental is conveniently located at 17 Regal Place, East Perth, close to Perth CBD. Our East Perth car rental location provides easy vehicle pick-up and return for daily, weekly and long-term car hire.
                  </p>
                  <p>
                    Use the live map to plan your route and check the confirmed dates and pick-up time in your booking before travelling. Contact the TAK8 Car Rental team if you need assistance planning your visit.
                  </p>
                </div>

                <address className="HomeMapAddress">
                  <strong>TAK8 Car Rental</strong>
                  <span>17 Regal Place, East Perth WA</span>
                </address>

                <a
                  className="HomeMapDirections"
                  href="https://maps.app.goo.gl/9an8CLaNvUhWqLnW7"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
                  Open in Google Maps
                </a>
              </div>

              <div className="HomeMapFrame">
                <iframe
                  title="Map showing TAK8 Car Rental at 17 Regal Place, East Perth"
                  src="https://www.google.com/maps?q=17%20Regal%20Place%2C%20East%20Perth%20WA&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>

              <div className="HomeLocalCard">
                <div className="HomeLocalCardHeader">
                  <span className="HomeLocalLogo">
                    <img
                      src="/img/logo.png"
                      alt="TAK8 Car Rental"
                      width="112"
                      height="40"
                    />
                  </span>
                  <div>
                    <span>TAK8 Car Rental</span>
                    <strong>East Perth Head Office</strong>
                  </div>
                </div>

                <address className="HomeLocalDetails">
                  <div className="HomeLocalDetail">
                    <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
                    <div>
                      <span>Address</span>
                      <a
                        href="https://maps.app.goo.gl/9an8CLaNvUhWqLnW7"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        17 Regal Place, East Perth WA
                      </a>
                    </div>
                  </div>

                  <div className="HomeLocalDetail">
                    <i className="far fa-clock" aria-hidden="true"></i>
                    <div>
                      <span>Office hours</span>
                      <strong>Monday–Friday, 9:00 AM–5:00 PM</strong>
                    </div>
                  </div>

                  <div className="HomeLocalDetail HomeLocalContactDetail">
                    <div>
                      <span>Contact</span>
                      <a className="HomeLocalPhoneLink" href="tel:+61409521554" aria-label="Call TAK8 Car Rental mobile">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path fill="currentColor" d="M7 1.75h10A2.25 2.25 0 0 1 19.25 4v16A2.25 2.25 0 0 1 17 22.25H7A2.25 2.25 0 0 1 4.75 20V4A2.25 2.25 0 0 1 7 1.75Zm0 2a.25.25 0 0 0-.25.25v14.5h10.5V4a.25.25 0 0 0-.25-.25H7Zm5 17.3a.8.8 0 1 0 0-1.6.8.8 0 0 0 0 1.6Z" />
                        </svg>
                        +61 409 521 554
                      </a>
                      <a className="HomeLocalPhoneLink" href="tel:+61893415334" aria-label="Call TAK8 Car Rental landline">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path fill="currentColor" d="M6.2 4.25h11.6c.8 0 1.45.65 1.45 1.45v2.05c0 .55-.45 1-1 1h-2.1a1 1 0 0 1-1-1V7H8.85v.75a1 1 0 0 1-1 1h-2.1a1 1 0 0 1-1-1V5.7c0-.8.65-1.45 1.45-1.45ZM8.4 9.5h7.2l3.15 4.15v5.1a1 1 0 0 1-1 1H6.25a1 1 0 0 1-1-1v-5.1L8.4 9.5Zm3.6 3a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" />
                        </svg>
                        +61 8 9341 5334
                      </a>
                    </div>
                  </div>

                  <div className="HomeLocalDetail">
                    <i className="far fa-envelope" aria-hidden="true"></i>
                    <div>
                      <span>Email</span>
                      <a href="mailto:info@tak8.com.au">info@tak8.com.au</a>
                    </div>
                  </div>
                </address>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

    </>
  );
};

export default Home;
